import { datadogLogs } from "@datadog/browser-logs"
import { NotificationProvider } from "@smoothincorg/smooth-ui"
import { AppProps } from "next/app"
import { useRouter } from "next/router"
import { useEffect } from "react"
import { RecoilRoot } from "recoil"
import { IS_DEV_ENV } from "src/components/EnvBanner"
import { AuthProvider } from "src/features/auth"
import { ENV_NAME } from "src/lib/constants"
import { DATADOG_PARAMS } from "src/lib/constants/values"
import { beforeSendLogs, usePageViewLog } from "src/lib/monitoring"
import "src/lib/styles/global.scss"
import { GetTokenFromCookies } from "src/lib/utils/cookies"
import { AppStateProvider } from "src/store/AppStateContext"
import { SidebarProvider } from "src/store/SidebarContext"
import { SWRConfig } from "swr"

const canBypassAuth =
  process.env.ENV_NAME === "local" && process.env.ENABLE_LOCAL_AUTH_BYPASS === "true"

const DEV_TITLE_PREFIX = "[DEV] "

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter()
  const pathname = router.pathname

  useEffect(() => {
    if (canBypassAuth) return
    const token = GetTokenFromCookies()
    if (!token && pathname !== "/admin/sign-in") {
      router.replace(
        "/admin/sign-in?" +
          new URLSearchParams({
            "signed-out": "1",
            "redirect-to": pathname,
          })
      )
    }
  }, [router, pathname])

  useEffect(() => {
    if (!IS_DEV_ENV) return

    const applyPrefix = () => {
      if (!document.title.startsWith(DEV_TITLE_PREFIX)) {
        document.title = DEV_TITLE_PREFIX + document.title
      }
    }
    applyPrefix()

    // next/headが各ページ遷移時に書き換える<title>を監視し、都度prefixを付け直す
    const titleElement = document.querySelector("title")
    if (!titleElement) return

    const observer = new MutationObserver(applyPrefix)
    observer.observe(titleElement, { childList: true, characterData: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!DATADOG_PARAMS.clientToken || ENV_NAME === "local") return

    datadogLogs.init({
      clientToken: DATADOG_PARAMS.clientToken,
      site: DATADOG_PARAMS.site,
      forwardErrorsToLogs: true,
      beforeSend: beforeSendLogs,
      sessionSampleRate: DATADOG_PARAMS.sessionSampleRate,
      env: ENV_NAME,
      service: DATADOG_PARAMS.service,
    })
    // init の env はタグにしかならないため、smooth_api のログ（lograge）と同じく属性 @env としても載せる
    datadogLogs.setGlobalContextProperty("env", ENV_NAME)
  }, [])

  // Logs の init より後に登録する（effect は記述順に動く）
  usePageViewLog()

  return (
    <AppStateProvider>
      <SWRConfig
        value={{
          revalidateIfStale: false,
          revalidateOnFocus: false,
          revalidateOnReconnect: false,
        }}
      >
        <AuthProvider>
          <RecoilRoot>
            <SidebarProvider>
              <NotificationProvider>
                <Component {...pageProps} />
              </NotificationProvider>
            </SidebarProvider>
          </RecoilRoot>
        </AuthProvider>
      </SWRConfig>
    </AppStateProvider>
  )
}

export default App
